/**
 * What a save operation answers with.
 *
 * Every operation returns a result rather than throwing, for the same reason the inventory does:
 * a caller that has to catch cannot tell a full disk from a corrupt record from a save written by
 * next month's build, and those are three different things to say to a player.
 *
 * A failure carries what explains it. `sectionRejected` carries the section's own failure verbatim
 * rather than flattening it to a string, so the screen that eventually reports "your save names an
 * item this build no longer has" still knows which item. `saveFailureText.ts` turns any of these
 * into the sentence a player reads, and it is the only place wording lives.
 *
 * The last four reasons belong to a file a player picked rather than to a record off disk. They are
 * here rather than in a union of their own because an import ends in the same place a load does,
 * and one screen reporting two failure types would need two switches to say one thing.
 */

import type { InventoryFailure } from '@/sim/inventory/inventoryResult';
import type { ProgressionFailure } from '@/sim/progression/progressionResult';

export type SaveFailure =
  /** The record off disk is not the shape a profile is. `field` is the first thing that was not. */
  | { readonly reason: 'malformed'; readonly field: string }
  /** Written by a build newer than this one. Never migrated, never overwritten. */
  | { readonly reason: 'fromNewerBuild'; readonly found: number; readonly current: number }
  /** Old enough that the chain has a hole in it. `missingFrom` is the step nobody wrote. */
  | { readonly reason: 'noMigrationPath'; readonly found: number; readonly missingFrom: number }
  /**
   * A section refused the data it was handed, and said why in its own vocabulary.
   *
   * One variant per section rather than one variant with a union of failures, so `section` and
   * `failure` cannot disagree: an inventory failure filed under progression does not compile.
   */
  | {
      readonly reason: 'sectionRejected';
      readonly section: 'inventory';
      readonly failure: InventoryFailure;
    }
  | {
      readonly reason: 'sectionRejected';
      readonly section: 'progression';
      readonly failure: ProgressionFailure;
    }
  /** The browser will not store this much. The one storage failure a player can act on. */
  | { readonly reason: 'quotaExceeded'; readonly profileId: string }
  /** No IndexedDB at all: a private window, a disabled store, an ancient engine. */
  | { readonly reason: 'storageUnavailable'; readonly detail: string }
  /** Storage was there and the operation still failed. `operation` names which one. */
  | { readonly reason: 'storageFailed'; readonly operation: string; readonly detail: string }
  | { readonly reason: 'noSuchProfile'; readonly profileId: string }
  /** A file the player picked that is not JSON at all. `detail` is what the parser said. */
  | { readonly reason: 'notJson'; readonly detail: string }
  /** JSON, but not an export from this game. `found` is whatever the `format` field held. */
  | { readonly reason: 'notASaveFile'; readonly found: string }
  /** An export written by a newer build's wrapper. The profile inside is never looked at. */
  | { readonly reason: 'fromNewerFormat'; readonly found: number; readonly current: number }
  /** Too big to be a save, so it is never read into a string. */
  | { readonly reason: 'fileTooLarge'; readonly bytes: number; readonly limit: number };

export type SaveResult<T> =
  { readonly ok: true; readonly value: T } | { readonly ok: false; readonly failure: SaveFailure };

export function saved<T>(value: T): SaveResult<T> {
  return { ok: true, value };
}

export function refused<T>(failure: SaveFailure): SaveResult<T> {
  return { ok: false, failure };
}
